// @ts-strict-ignore
import { ColumnPicker } from "@dashboard/components/Datagrid/ColumnPicker/ColumnPicker";
import { useColumns } from "@dashboard/components/Datagrid/ColumnPicker/useColumns";
import Datagrid from "@dashboard/components/Datagrid/Datagrid";
import {
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { useEmptyColumn } from "@dashboard/components/Datagrid/hooks/useEmptyColumn";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination";
import { SubscriptionListUrlSortField } from "@dashboard/orders/urls";
import { SubscriptionRow } from "@dashboard/orders/views/OrderSubscriptionsList/OrderSubscriptionsList.utils";
import { ListProps, SortPage } from "@dashboard/types";
import { Item } from "@glideapps/glide-data-grid";
import { Box } from "@saleor/macaw-ui-next";
import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";

import {
  subscriptionListStaticColumnAdapter,
  useGetCellContent,
} from "./datagrid";
import { messages } from "./messages";
import { canBeSorted, getSubscriptionsRowsLength } from "./utils";

interface OrderSubscriptionsListDatagridProps
  extends ListProps,
    SortPage<SubscriptionListUrlSortField> {
  rows: SubscriptionRow[];
  rowAnchor?: (id: string) => string;
}

export const OrderSubscriptionsListDatagrid: React.FC<
  OrderSubscriptionsListDatagridProps
> = ({
  rows,
  disabled,
  settings,
  onUpdateListSettings,
  onSort,
  sort,
  rowAnchor,
}) => {
  const intl = useIntl();
  const datagrid = useDatagridChangeState();
  const rowsLength = getSubscriptionsRowsLength(rows, disabled);

  const handleColumnChange = useCallback(
    picked => {
      onUpdateListSettings("columns", picked.filter(Boolean));
    },
    [onUpdateListSettings],
  );

  const emptyColumn = useEmptyColumn();
  const memoizedStaticColumns = useMemo(
    () => subscriptionListStaticColumnAdapter(emptyColumn, intl, sort),
    [emptyColumn, intl, sort],
  );

  // Migrate older saved column presets that were stored before
  // the "originalOrderDate" column existed.
  const selectedColumnsWithMigration = useMemo(() => {
    const selected = settings?.columns ?? [];
    if (
      selected.length === 6 &&
      selected.includes("order") &&
      selected.includes("customer") &&
      selected.includes("status") &&
      selected.includes("nextPayment") &&
      selected.includes("interval") &&
      selected.includes("amount") &&
      !selected.includes("originalOrderDate")
    ) {
      return [...selected, "originalOrderDate"];
    }
    return selected;
  }, [settings?.columns]);

  const { handlers, staticColumns, visibleColumns, selectedColumns } =
    useColumns({
      staticColumns: memoizedStaticColumns,
      selectedColumns: selectedColumnsWithMigration,
      onSave: handleColumnChange,
    });

  const handleHeaderClick = useCallback(
    (col: number) => {
      const columnName = visibleColumns[col].id as SubscriptionListUrlSortField;

      if (canBeSorted(columnName)) {
        onSort(columnName);
      }
    },
    [visibleColumns, onSort],
  );

  const handleRowAnchor = useCallback(
    ([, row]: Item) => {
      if (!rowAnchor) {
        return;
      }
      const rowData = rows[row];
      return rowData ? rowAnchor(rowData.id) : undefined;
    },
    [rowAnchor, rows],
  );

  const getCellContent = useGetCellContent({
    columns: visibleColumns,
    rows,
  });

  return (
    <Box __marginTop={rowsLength > 0 ? -1 : 0}>
      <DatagridChangeStateContext.Provider value={datagrid}>
        <Datagrid
          readonly
          rowMarkers="none"
          loading={disabled}
          columnSelect="single"
          hasRowHover
          freezeColumns={2}
          verticalBorder={col => col > 1}
          availableColumns={visibleColumns}
          onHeaderClicked={handleHeaderClick}
          emptyText={intl.formatMessage(messages.emptyText)}
          getCellContent={getCellContent}
          getCellError={() => false}
          menuItems={() => []}
          rows={rowsLength}
          selectionActions={() => null}
          onColumnResize={handlers.onResize}
          onColumnMoved={handlers.onMove}
          renderColumnPicker={() => (
            <ColumnPicker
              staticColumns={staticColumns}
              selectedColumns={selectedColumns}
              onToggle={handlers.onToggle}
            />
          )}
          fullScreenTitle={intl.formatMessage(messages.subscriptions)}
          rowAnchor={handleRowAnchor}
        />

        <Box paddingX={6}>
          <TablePaginationWithContext
            component="div"
            settings={settings}
            disabled={disabled}
            onUpdateListSettings={onUpdateListSettings}
          />
        </Box>
      </DatagridChangeStateContext.Provider>
    </Box>
  );
};
